import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import UpSellModal from '../modals/UpSellModal';
import OrderBasketItem from '../components-library/basket/OrderBasketItem';
import {
  DeliveryMode,
  ERROR_CODES,
  ORDER_METHODS,
  PROMO_CODE_DISCOUNT_TYPES,
  PROMO_CODE_TYPES,
  SCREENS,
} from '../services/exports/Constants';
import CheckoutNavBar from '../components-library/navigation/CheckoutNavBar';
import { useSelector } from 'react-redux';
import { ProductInterface } from '../services/exports/Interfaces';
import CompanyManager from '../services/api/CompanyManager';
import useScreenType from '../hooks/utility/useScreenType';
import useTheme from '../hooks/ui/useTheme';
import DeliveryThreshold from '../components-library/delivery/DeliveryThreshold';
import useDeliveryThreshold from '../hooks/order/useDeliveryThreshold';
import OrderValidationManager from '../services/api/OrderValidationManager';
import CheckoutThreshold from '../components-library/checkout/CheckoutThreshold';
import useCheckoutThreshold from '../hooks/order/useCheckoutThreshold';
import CartFooter from '../components-library/footers/CartFooter';
import CheckoutButton from '../components-library/buttons/CheckoutButton';
import { Trans, useTranslation } from 'react-i18next';
import useInitialData from '../hooks/global/useInitialData';
import useNavigate from '../hooks/navigation/useNavigate';
import useRouteMatch from '../hooks/navigation/useRouteMatch';
import useBasket from '../hooks/basket/useBasket';
import { StoreInterface } from '../store/types';
import EmptyBasketIllustration from '../assets/illustrations/EmptyBasketIllustration';
import OrderType from '../components-library/order-type/OrderType';
import useAppMethods from '../hooks/utility/useAppMethods';
import useOrderMethodsStatus from '../hooks/availability/useOrderMethodsStatus';
import moment from 'moment';
import LabelIcon from '../assets/logo/LabelIcon';
import PromoCodeInput from '../components-library/checkout/PromoCodeInput';
import classnames from 'classnames';
import useOrderFlow from '../hooks/order/useOrderFlow';
import { CompleteBasketItem } from '../providers/BasketProvider';

interface Props {
  customParentContainer?: string;
  customParentStyle?: object;
}

export default function OrderBasketScreen(props: Props) {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderBasketScreen' });

  const { customParentContainer, customParentStyle } = props;

  const { data, refresh } = useInitialData();
  const { company } = data;
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  const theme = useTheme();
  const { isDesktop, isMobile } = useScreenType();
  const routeMatch = useRouteMatch();
  const { navigate } = useNavigate();

  const { isOrderMethodInfoComplete, getOrderData } = useOrderFlow();
  const { hasReachedCheckoutThreshold } = useCheckoutThreshold();
  const { hasDeliveryThreshold, isWithinThreshold } = useDeliveryThreshold();
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus();
  const { items, refreshBasket, getProductIds } = useBasket();
  const { toggleOrderMethodModal, formatCurrency } = useAppMethods();

  const [showUpSellModal, setShowUpSellModal] = useState(false);
  const [upSellProducts, setUpSellProducts] = useState<ProductInterface[]>([]);
  const [validating, setValidating] = useState(false);
  const [loadingUpSellProducts, setLoadingUpSellProducts] = useState(false);

  type PromoCodeInputRefType = {
    setPromoCode: (promoCode: any) => void;
  };

  const promoCodeInputRef = useRef<PromoCodeInputRefType>();

  const orderMethodInfoComplete =
    isOrderMethodInfoComplete &&
    (cached_order?.method === ORDER_METHODS.PICKUP ||
      (cached_order?.method === ORDER_METHODS.ROOM_SERVICE && !!cached_order?.room_service_location_id) ||
      (cached_order?.method === ORDER_METHODS.DELIVERY &&
        company?.delivery_mode === DeliveryMode.Internal &&
        !!cached_order?.delivery_zone?.id) ||
      (cached_order?.method === ORDER_METHODS.DELIVERY &&
        company?.delivery_mode === DeliveryMode.External &&
        !!cached_order?.delivery_zone?.id &&
        !!cached_order?.delivery_quote?.id));

  useEffect(() => {
    window.scrollTo(0, 0);
    refreshBasket();
  }, []);

  function toggleUpSellModal(products?: ProductInterface[]) {
    setShowUpSellModal(!showUpSellModal);
    if (products) {
      setUpSellProducts(products);
    }
  }

  async function validateBeforeProceeding(): Promise<void> {
    setValidating(true);
    if (!(await isValid())) {
      return setValidating(false);
    }
    setValidating(false);

    onProceed();
  }

  function onProceed(): Promise<void> {
    if (!isDesktop || routeMatch(SCREENS.HOME_SCREEN)) {
      return requestSuggestions();
    }

    navigate(SCREENS.CHECKOUT);
  }

  async function isValid(): Promise<boolean> {
    if (!orderMethodInfoComplete) {
      toggleOrderMethodModal({ openModal: true });

      return false;
    }

    if (
      cached_order.method === ORDER_METHODS.ROOM_SERVICE &&
      ((!company?.table_number_hidden ? !cached_order.room_service_details : false) ||
        !cached_order.room_service_location_id)
    ) {
      toggleOrderMethodModal({ openModal: true });

      return false;
    }

    if (cached_order?.scheduled_for && moment(cached_order?.scheduled_for).isBefore(moment())) {
      handleOrderTimeNotAvailable();

      return false;
    }

    return new OrderValidationManager().validate(getOrderData(), {
      [ERROR_CODES.ORDER_TIME_NOT_AVAILABLE]: handleOrderTimeNotAvailable,
      [ERROR_CODES.PICKUP_CLOSED]: handleOrderTimeNotAvailable,
      [ERROR_CODES.DELIVERY_CLOSED]: handleOrderTimeNotAvailable,
      [ERROR_CODES.ITEMS_OUT_OF_STOCK]: handleItemsOutOfStock,
      [ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE]: handleAddressOutsideDeliveryArea,
    });
  }

  function handleOrderTimeNotAvailable(): void {
    navigate(SCREENS.HOME_SCREEN);
    toggleOrderMethodModal({ openModal: true });
  }

  function handleItemsOutOfStock(): Promise<void> | void {
    return refresh().then(() => navigate(SCREENS.HOME_SCREEN));
  }

  function handleAddressOutsideDeliveryArea(): void {
    navigate(SCREENS.HOME_SCREEN);
    toggleOrderMethodModal({ openModal: true });
  }

  async function requestSuggestions(): Promise<void> {
    setLoadingUpSellProducts(true);

    const { response, success } = await CompanyManager.getSuggestedProducts(company?.id, getProductIds());

    setLoadingUpSellProducts(false);
    if (success && response.data.data.length > 0) {
      return toggleUpSellModal(response.data.data);
    }

    navigate(SCREENS.CHECKOUT);
  }

  const renderCheckoutButton = (): ReactElement => {
    const disabled =
      !hasReachedCheckoutThreshold ||
      (hasDeliveryThreshold && !isWithinThreshold) ||
      (!isOrderMethodCurrentlyOpen(cached_order?.method) && !isOrderMethodOpenForFutureOrders(cached_order?.method));

    return (
      <div className="sticky bottom-0 flex-none">
        {isDesktop ? (
          <CheckoutButton
            title={t('buttons.checkout')}
            onClick={validateBeforeProceeding}
            loading={validating}
            disabled={disabled}
          />
        ) : (
          <CartFooter
            title={t('buttons.checkout')}
            onClick={validateBeforeProceeding}
            loading={validating || loadingUpSellProducts}
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  const renderPromoCodeBanner = (): JSX.Element | null => {
    const promoCode = company?.promo_codes?.length ? company.promo_codes[0] : null;

    if (!promoCode || !company?.show_master_promo_code) {
      return null;
    }

    const isApplied = cached_order?.coupon_details?.code === promoCode.code;

    const applyPromoCode = () => {
      promoCodeInputRef.current.setPromoCode(promoCode);
    };

    const formattedDiscount =
      promoCode.discount_type === PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE
        ? promoCode.components.find((component) => component.is_used === false).discount + '%'
        : formatCurrency(promoCode.discount, { trimInteger: true });

    const renderRedeemButton = () => {
      return (
        <div
          className="rounded-full bg-white border border-primary mt-xs p-xs flex justify-center cursor-pointer"
          onClick={applyPromoCode}
        >
          <span className="font-medium text-primary text-2xs">{t('promo_code_banner.redeem')}</span>
        </div>
      );
    };

    const renderDisabledButton = () => {
      return (
        <div className="rounded-full bg-gray mt-xs p-xs flex justify-center">
          <span className="font-medium text-white text-2xs">{t('promo_code_banner.redeemed')}</span>
        </div>
      );
    };

    return (
      <div className="mt-md p-sm bg-green-300 rounded-xl">
        <div className="py-2xs px-xs bg-primary w-fit rounded flex items-center">
          <LabelIcon className="w-3 h-3 mr-2xs" color={theme.color.white.DEFAULT} />
          <span className="text-2xs font-medium text-white">Code</span>
        </div>
        <p className="mt-xs text-xs font-medium text-primary">
          {(promoCode.type === PROMO_CODE_TYPES.ONE_TIME_PROMO_CODE ||
            promoCode.discount_type === PROMO_CODE_DISCOUNT_TYPES.PERCENTAGE) &&
            t('promo_code_banner.single_use', {
              discount: formattedDiscount,
              code: promoCode.code,
            })}

          {promoCode.type === PROMO_CODE_TYPES.MULTI_PROMO_CODE &&
            promoCode.discount_type === PROMO_CODE_DISCOUNT_TYPES.FIXED_AMOUNT &&
            t('promo_code_banner.multi_use', {
              discount: formattedDiscount,
              count: promoCode.components.length,
              code: promoCode.code,
            })}
        </p>
        {isApplied ? renderDisabledButton() : renderRedeemButton()}
      </div>
    );
  };

  const renderEmptyBasket = (): JSX.Element => {
    return (
      <div className="grow w-full flex flex-col justify-center items-center z-1">
        <EmptyBasketIllustration />
        <p className="text-center text-sm text-gray-700">
          <Trans t={t}>empty_order_basket.description</Trans>
        </p>
      </div>
    );
  };

  const renderUpSellModal = useMemo<JSX.Element>(() => {
    return <UpSellModal openModal={showUpSellModal} products={upSellProducts} toggleModal={toggleUpSellModal} />;
  }, [showUpSellModal, upSellProducts]);

  return (
    <div className={customParentContainer ?? parentContainer} style={customParentStyle}>
      <div className="h-full flex-initial overflow-y-auto">
        {!isDesktop && (
          <CheckoutNavBar title={t('header')} showLogoOnMobile goBack={() => navigate(SCREENS.HOME_SCREEN)} />
        )}
        <div className="p-md">
          <div className="bg-white p-sm rounded-2xl">
            {isDesktop && <OrderType className="mb-md" />}
            <PromoCodeInput ref={promoCodeInputRef} />
            {renderPromoCodeBanner()}
          </div>
          {items.length > 0 ? (
            <div className="mt-md">
              {items.map((item: CompleteBasketItem, index) => (
                <div
                  className={classnames({ 'mb-md': index !== items.length - 1 ? true : !isMobile })}
                  key={`basket-item-${index}`}
                >
                  <OrderBasketItem data={item} index={index} editable />
                </div>
              ))}
              <DeliveryThreshold className="mt-md lg:mb-md" />
              <CheckoutThreshold className="mt-md lg:mb-md" />
            </div>
          ) : (
            renderEmptyBasket()
          )}
        </div>
      </div>
      {renderCheckoutButton()}
      {renderUpSellModal}
    </div>
  );
}

const parentContainer = 'lg:h-screen h-full flex flex-col bg-gray-600';
